export const DEFAULT_COUNTRY_CODE = '+91';

const collapseWhitespace = (value: string) => value.trim().replace(/\s+/g, ' ');

export const PHONE_NUMBER_ERROR_MESSAGE =
  'Phone number must include a country code, like +91 9876543210';

export function normalizeCountryCode(value?: string | null, fallback = DEFAULT_COUNTRY_CODE) {
  const fallbackDigits = fallback.replace(/\D/g, '');
  const digits = (value ?? '').replace(/\D/g, '');

  if (!digits) {
    return fallbackDigits ? `+${fallbackDigits}` : fallback;
  }

  return `+${digits.slice(0, 4)}`;
}

export function normalizePhoneNumberPart(value?: string | null) {
  return (value ?? '').replace(/\D/g, '');
}

export function splitPhoneNumber(
  value?: string | null,
  fallbackCountryCode = DEFAULT_COUNTRY_CODE
) {
  const normalizedFallback = normalizeCountryCode(fallbackCountryCode, DEFAULT_COUNTRY_CODE);
  const trimmed = value?.trim() ?? '';

  if (!trimmed) {
    return {
      countryCode: normalizedFallback,
      phoneNumber: '',
    };
  }

  const collapsed = collapseWhitespace(trimmed);

  if (/^\+\d{1,4}$/.test(collapsed)) {
    return {
      countryCode: normalizeCountryCode(collapsed, normalizedFallback),
      phoneNumber: '',
    };
  }

  if (collapsed.includes(' ')) {
    const [countryCodeRaw, ...phoneParts] = collapsed.split(' ');

    return {
      countryCode: normalizeCountryCode(countryCodeRaw, normalizedFallback),
      phoneNumber: normalizePhoneNumberPart(phoneParts.join(' ')),
    };
  }

  const digits = normalizePhoneNumberPart(collapsed);

  if (!digits) {
    return {
      countryCode: normalizedFallback,
      phoneNumber: '',
    };
  }

  const fallbackDigits = normalizedFallback.replace(/\D/g, '');

  if (collapsed.startsWith('+') && fallbackDigits && digits.startsWith(fallbackDigits)) {
    return {
      countryCode: normalizedFallback,
      phoneNumber: digits.slice(fallbackDigits.length),
    };
  }

  return {
    countryCode: normalizedFallback,
    phoneNumber: digits,
  };
}

export function combinePhoneNumber(
  countryCode?: string | null,
  phoneNumber?: string | null,
  options?: {
    fallbackCountryCode?: string;
    keepCountryCodeWhenEmpty?: boolean;
  }
) {
  const fallbackCountryCode = options?.fallbackCountryCode ?? DEFAULT_COUNTRY_CODE;
  const normalizedCountryCode = normalizeCountryCode(countryCode, fallbackCountryCode);
  const digits = normalizePhoneNumberPart(phoneNumber);

  if (!digits) {
    return options?.keepCountryCodeWhenEmpty ? normalizedCountryCode : '';
  }

  return `${normalizedCountryCode} ${digits}`;
}

export function isPhoneValueEmptyLike(value?: string | null) {
  const collapsed = collapseWhitespace(value ?? '');
  return collapsed === '' || /^\+\d{1,4}$/.test(collapsed);
}

export function isPhoneValueWithCountryCode(value?: string | null) {
  const collapsed = collapseWhitespace(value ?? '');

  if (!collapsed || isPhoneValueEmptyLike(collapsed)) {
    return false;
  }

  if (!collapsed.startsWith('+')) {
    return false;
  }

  const digits = collapsed.replace(/\D/g, '');
  return digits.length >= 8 && digits.length <= 15;
}

export function normalizePhoneNumber(
  value?: string | null,
  fallbackCountryCode = DEFAULT_COUNTRY_CODE
) {
  const rawValue = value?.trim() ?? '';

  if (!rawValue) {
    return null;
  }

  const normalizedValue = rawValue.startsWith('00') ? `+${rawValue.slice(2)}` : rawValue;
  const digitsOnly = normalizePhoneNumberPart(normalizedValue);

  if (!digitsOnly) {
    return null;
  }

  if (normalizedValue.startsWith('+')) {
    return digitsOnly.length >= 8 && digitsOnly.length <= 15 ? `+${digitsOnly}` : null;
  }

  const normalizedFallback = normalizeCountryCode(fallbackCountryCode, DEFAULT_COUNTRY_CODE);
  const fallbackDigits = normalizedFallback.replace(/\D/g, '');
  const localDigits = digitsOnly.replace(/^0+(?=\d{8,}$)/, '');

  if (!localDigits) {
    return null;
  }

  const combinedDigits =
    fallbackDigits &&
    localDigits.startsWith(fallbackDigits) &&
    localDigits.length > fallbackDigits.length + 6
      ? localDigits
      : `${fallbackDigits}${localDigits}`;

  return combinedDigits.length >= 8 && combinedDigits.length <= 15 ? `+${combinedDigits}` : null;
}

export function formatPhoneHref(value?: string | null) {
  const digits = (value ?? '').trim().replace(/\D/g, '');

  if (!digits) {
    return '';
  }

  return `tel:+${digits}`;
}
